import React from 'react';
import moment from 'moment/moment';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { ArrowLeftIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';
import { ContactPersonAddEdit } from 'components/common/contactPerson/addEdit';
import { Button, Col, Collapse, Divider, Form, HasAccess, Row, Spin, Typography, Upload } from 'components';
import { PublishSupplierOfferModal } from './publishSupplierOffer';
import { SupplierOfferItemsTable } from './SupplierOfferItems';
import { ShippingInformation } from './shippingInformation';
import { GeneralInformation } from './generalInformation';
import { RemoveBrandModal } from './removeBrand';
import { FeesAndCharges } from './feesAndCharges';
import styles from './../styles.module.scss';
import { AddBrandModal } from './addBrand';
import { useEdit } from './useEdit';

const { Title, Text } = Typography;

export const CustomSupplierOfferEdit = React.memo(() => {
  const {
    form,
    handleEmailUpload,
    emailPreview,
    attachments,
    handleFormChange,
    setAttachments,
    selectedSupplier,
    contactPersons,
    isLoadingContactPerson,
    handleFormItemBlur,
    handleFormItemChange,
    supplierOfferId,
    onAttachmentAdd,
    onAttachmentDelete,
    setOpenContactPersonModal,
    openContactPersonModal,
    onDraft,
    globalShippingCost,
    leadType,
    visibleColumns,
    setVisibleColumns,
    supplierOffer,
    recordId,
    onPublish,
    publishModalState,
    supplierOfferItems,
    addBrandModalState,
    onAddBrandModalStateChange,
    onRemoveBrandModalStateChange,
    removeBrandModalState,
    removeBrandId,
    source,
    entityId,
    supplierOfferLoadingState,
    onPublishModalClose,
  } = useEdit();
  return (
    <>
      <Spin spinning={supplierOfferLoadingState} indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}>
        <Row gutter={[0, 32]} style={{ padding: '20px 28px 20px 60px' }} className={'margin-t-20'}>
          <Col span={24}>
            <Form form={form} onValuesChange={handleFormChange} layout="vertical" requiredMark={false}>
              <Row gutter={[0, 24]}>
                <Col span={24}>
                  <Row align={'middle'} justify={'center'}>
                    <Col className={styles.back_btn}>
                      <Button
                        title={'Click to go back'}
                        onClick={() => history.back()}
                        type={'link'}
                        icon={<ArrowLeftIcon />}
                      />
                    </Col>
                    <Title level={2} className={'reset-title font-weight-800 center'}>
                      Update Supplier Offer
                    </Title>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 24]}>
                    <Col span={24}>
                      <Form.Item
                        name="emailFile"
                        dependencies={['source']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const source = getFieldValue('source');
                              if (source === 'EMAIL' && (!value || value.length === 0)) {
                                return Promise.reject(ValidationErrors.required('Email'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Upload.Dragger
                          accept=".eml"
                          className={'email-upload'}
                          beforeUpload={handleEmailUpload}
                          maxCount={1}
                          showUploadList={false}
                          style={{ marginBottom: '16px' }}
                        >
                          <Row style={{ padding: '34px' }} justify={'center'}>
                            <Text className={'font-size-18 font-weight-700'}>Upload Email</Text>
                          </Row>
                        </Upload.Dragger>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      {emailPreview && (
                        <Collapse
                          className={styles.collapse}
                          accordion
                          expandIconPosition="end"
                          items={[
                            {
                              key: 'supplier-email',
                              label: 'Supplier Email',
                              children: (
                                <Row gutter={[0, 32]}>
                                  <Col span={24}>
                                    <Row justify="space-between">
                                      <Col>
                                        <Row gutter={24}>
                                          <Col>
                                            <Text>
                                              <strong>Subject:</strong> <br />
                                              {emailPreview.subject}
                                            </Text>
                                          </Col>
                                          <Col>
                                            <Text>
                                              <strong>From:</strong>
                                              <br />
                                              {emailPreview.from}
                                            </Text>
                                          </Col>
                                        </Row>
                                      </Col>
                                      <Col>
                                        <Text>{moment(emailPreview.dateTime).format('DD.MM.YYYY HH:mm')}</Text>
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col span={24}>
                                    <iframe
                                      srcDoc={emailPreview.body?.replace(
                                        /<a\s+(?![^>]*target=)/g,
                                        '<a target="_blank" '
                                      )}
                                      className="html-content-iframe"
                                      sandbox="allow-popups"
                                    />
                                  </Col>
                                </Row>
                              ),
                            },
                          ]}
                        />
                      )}
                    </Col>
                    <Col span={24}>
                      <Row align={'middle'} className={'gap-16'}>
                        <Link
                          to={`${ADMIN_ROUTES.suppliers.view}/${supplierOffer.supplierId}/general`}
                          target={'_blank'}
                        >
                          <Text
                            underline
                            type={'secondary'}
                            className={'font-weight-600 font-size-16'}
                            ellipsis={{ tooltip: true }}
                          >
                            {supplierOffer.supplierName}
                          </Text>
                        </Link>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={28}>
                        <Col span={12}>
                          <GeneralInformation
                            handleFormItemBlur={handleFormItemBlur}
                            handleFormItemChange={handleFormItemChange}
                            attachments={attachments}
                            setAttachments={setAttachments}
                            contactPersons={contactPersons}
                            isLoadingContactPerson={isLoadingContactPerson}
                            setOpenContactPersonModal={setOpenContactPersonModal}
                            onAttachmentAdd={onAttachmentAdd}
                            onAttachmentDelete={onAttachmentDelete}
                          />
                        </Col>
                        <Col span={12}>
                          <Row gutter={[0, 16]}>
                            <Col span={24}>
                              <Form.Item name="shippingInformation" noStyle>
                                <ShippingInformation
                                  handleFormItemBlur={handleFormItemBlur}
                                  handleFormItemChange={handleFormItemChange}
                                  form={form}
                                  leadType={leadType}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={24}>
                              <Form.Item name="fee" noStyle>
                                <FeesAndCharges handleFormItemBlur={handleFormItemBlur} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col span={24} className={'margin-t-8'}>
            <Row align={'middle'} justify={'space-between'}>
              <Text className={'font-size-20 font-weight-800'} style={{ color: '#056156' }}>
                Brands
              </Text>
              {/*<HasAccess role={PERMISSIONS.supplierOffers.supplierOffer.write}>*/}
              {/*  <Button*/}
              {/*    type={'primary'}*/}
              {/*    ghost*/}
              {/*    size={'middle'}*/}
              {/*    color={'primary'}*/}
              {/*    onClick={onAddBrandModalStateChange}*/}
              {/*    icon={<PlusIcon size={'small'} color={'#05796B'} />}*/}
              {/*  >*/}
              {/*    Add New*/}
              {/*  </Button>*/}
              {/*</HasAccess>*/}
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={[0, 13]}>
              {Object.keys(supplierOfferItems || {}).map((brandId: string, index: number) => {
                const supplierOfferBrandItems = supplierOfferItems[brandId] || [];
                return (
                  <Col span={24} key={brandId}>
                    <Collapse
                      key={brandId}
                      className={styles.collapse}
                      accordion
                      expandIconPosition="end"
                      items={[
                        {
                          key: 'supplier-email',
                          label: (
                            <Row justify={'space-between'} align={'middle'}>
                              <Col>
                                <Link
                                  to={`${ADMIN_ROUTES.brand.view}/${brandId}/management`}
                                  onClick={(e) => e.stopPropagation()} // prevent collapse toggle
                                  target={'_blank'}
                                >
                                  <Text underline className={'font-size-18 font-weight-800'}>
                                    {supplierOfferBrandItems[0].brandName}
                                  </Text>
                                </Link>
                              </Col>
                              {/*<Col>*/}
                              {/*  <Row align={'middle'}>*/}
                              {/*    <Button*/}
                              {/*      size={'small'}*/}
                              {/*      type={'link'}*/}
                              {/*      variant={'text'}*/}
                              {/*      icon={<RemoveIcon />}*/}
                              {/*      onClick={(event) => {*/}
                              {/*        onRemoveBrandModalStateChange(brandId);*/}
                              {/*        event.stopPropagation();*/}
                              {/*      }}*/}
                              {/*    />*/}
                              {/*  </Row>*/}
                              {/*</Col>*/}
                            </Row>
                          ),
                          children: (
                            <Row gutter={[0, 32]}>
                              <Col span={24}>
                                <SupplierOfferItemsTable
                                  originalForm={form}
                                  globalShippingCost={globalShippingCost}
                                  supplierOfferId={supplierOfferId}
                                  initialItems={supplierOfferBrandItems}
                                  setVisibleColumns={setVisibleColumns}
                                  visibleColumns={visibleColumns}
                                  brandId={brandId}
                                  showTableSettings={!index}
                                />
                              </Col>
                              {supplierOfferBrandItems.map((supplierOfferItem: Record<string, any>) => {
                                return Object.keys(supplierOfferItem.alternativeSubstituteItem || {}).map(
                                  (itemId: string) => {
                                    const _item = supplierOfferItem.alternativeSubstituteItem[itemId] || [];
                                    return (
                                      <Col span={24} key={supplierOfferItem.id}>
                                        <SupplierOfferItemsTable
                                          originalForm={form}
                                          globalShippingCost={globalShippingCost}
                                          subTable={true}
                                          originalItemId={supplierOfferItem.id}
                                          subTableType={supplierOfferItem.itemActionType}
                                          subTableBrand={supplierOfferItem.brandName}
                                          subTableBrandId={supplierOfferItem.brandId}
                                          subTableItemNumber={supplierOfferItem.itemNumber}
                                          subTableSku={supplierOfferItem.sku}
                                          supplierOfferId={supplierOfferId}
                                          initialItems={_item}
                                          setVisibleColumns={setVisibleColumns}
                                          visibleColumns={visibleColumns}
                                          brandId={brandId}
                                          showTableSettings={!index}
                                        />
                                      </Col>
                                    );
                                  }
                                );
                              })}
                            </Row>
                          ),
                        },
                      ]}
                    />
                  </Col>
                );
              })}
            </Row>
          </Col>
          <Divider className={styles.divider} />
          <Col span={24}>
            <Row justify={'end'} gutter={8}>
              <Col flex={'160px'}>
                <Button block size="large" type={'primary'} disabled={supplierOffer.published} ghost onClick={onDraft}>
                  Save Draft
                </Button>
              </Col>
              <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.write}>
                <Col flex={'160px'}>
                  <Button
                    size={'large'}
                    block
                    type={'primary'}
                    disabled={supplierOffer.published}
                    onClick={() => onPublish(supplierOffer.id)}
                  >
                    {supplierOffer.published ? 'Published' : 'Publish'}
                  </Button>
                </Col>
              </HasAccess>
            </Row>
          </Col>
        </Row>
      </Spin>
      <ContactPersonAddEdit
        entityName={'SUPPLIER'}
        modalState={openContactPersonModal}
        clientId={selectedSupplier}
        onModalStateChange={() => setOpenContactPersonModal((oState) => !oState)}
      />
      <PublishSupplierOfferModal
        openModal={publishModalState}
        onClose={onPublishModalClose}
        recordId={recordId}
        entityId={entityId}
        source={source}
      />
      <AddBrandModal
        openModal={addBrandModalState}
        onCancelModal={onAddBrandModalStateChange}
        recordId={supplierOffer.id}
      />
      <RemoveBrandModal
        openModal={removeBrandModalState}
        onCancelModal={onRemoveBrandModalStateChange}
        recordId={removeBrandId}
      />
    </>
  );
});
